/**
 * Near Me Filter Definitions
 *
 * Special filters (price, quality, food-type) are defined here.
 * Cuisine filters are generated dynamically from the database.
 */

export type NearMeFilterConfig = {
	title: string
	seoTitle: string
	description: string
	whereClause: Record<string, unknown>
}

// Price-focused filters
export const PRICE_FILTERS: Record<string, NearMeFilterConfig> = {
	'cheap-eats': {
		title: 'Cheap Eats Near You',
		seoTitle: 'Cheap Eats Near Me in London',
		description:
			'Affordable restaurants within walking distance. Great food without breaking the bank.',
		whereClause: { priceRange: '£' },
	},
	'mid-range': {
		title: 'Mid-Range Near You',
		seoTitle: 'Mid-Range Restaurants Near Me in London',
		description: 'Quality dining at reasonable prices nearby.',
		whereClause: { priceRange: '££' },
	},
	'date-night': {
		title: 'Date Night Near You',
		seoTitle: 'Date Night Restaurants Near Me in London',
		description: 'Romantic spots perfect for a special evening out.',
		whereClause: { priceRange: { in: ['££', '£££'] } },
	},
	'fine-dining': {
		title: 'Fine Dining Near You',
		seoTitle: 'Fine Dining Near Me in London',
		description: 'Premium dining experiences in your area.',
		whereClause: { priceRange: '££££' },
	},
}

// Quality-focused filters
export const QUALITY_FILTERS: Record<string, NearMeFilterConfig> = {
	'top-rated': {
		title: 'Top Rated Near You',
		seoTitle: 'Top Rated Restaurants Near Me in London',
		description: 'Highest-scoring restaurants closest to you.',
		whereClause: { consensusScore: { gte: 80 } },
	},
	'hidden-gems': {
		title: 'Hidden Gems Near You',
		seoTitle: 'Hidden Gem Restaurants Near Me in London',
		description: 'Under-the-radar spots that locals love.',
		whereClause: { isHiddenGem: true },
	},
}

// Food-type focused filters (generic food types, not cuisines)
export const FOOD_TYPE_FILTERS: Record<string, NearMeFilterConfig> = {
	coffee: {
		title: 'Coffee Near You',
		seoTitle: 'Coffee Shops Near Me in London',
		description: 'Great coffee within walking distance.',
		whereClause: { cuisine: { contains: 'Cafe' } },
	},
	brunch: {
		title: 'Brunch Near You',
		seoTitle: 'Brunch Spots Near Me in London',
		description: 'Weekend brunch spots nearby.',
		whereClause: { cuisine: { contains: 'Brunch' } },
	},
}

// All special (non-cuisine) filters
export const SPECIAL_FILTERS: Record<string, NearMeFilterConfig> = {
	...PRICE_FILTERS,
	...QUALITY_FILTERS,
	...FOOD_TYPE_FILTERS,
}

/**
 * Get a special filter config by slug
 * Returns null if not a special filter (might be a cuisine)
 */
export function getSpecialFilter(slug: string): NearMeFilterConfig | null {
	return SPECIAL_FILTERS[slug] ?? null
}

/**
 * Generate a filter config for a cuisine
 * Used when the filter slug is a dynamic cuisine from the database
 */
export function generateCuisineFilter(
	cuisineName: string,
	slug: string,
): NearMeFilterConfig {
	return {
		title: `${cuisineName} Near You`,
		seoTitle: `${cuisineName} Restaurants Near Me in London`,
		description: `Find the best ${cuisineName.toLowerCase()} restaurants closest to you.`,
		whereClause: { cuisine: { contains: cuisineName } },
	}
}

// Featured special filters to always show in chips
export const FEATURED_SPECIAL_FILTERS = [
	'cheap-eats',
	'top-rated',
	'coffee',
	'brunch',
] as const

// Special filters for sitemap (non-cuisine)
export const SITEMAP_SPECIAL_FILTERS = [
	'cheap-eats',
	'top-rated',
	'mid-range',
	'fine-dining',
	'hidden-gems',
	'coffee',
	'brunch',
] as const
